// pages/Profile.js - Updated with Guest List icon
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiMapPin, 
  FiEdit2, 
  FiShare2, 
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiGlobe,
  FiCalendar,
  FiUsers,
  FiHome,
  FiUser as FiUserIcon,
  FiSettings,
  FiChevronRight,
  FiMail,
  FiBell,
  FiFileText,
  FiTrendingUp,
  FiList
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/Profile.scss';
import { parseLocalDate } from '../shared/eventDateTime';

/**
 * The You tab's menu.
 *
 * Order is by how often people reach for them, not alphabetically: cards and
 * guest lists are things you make, notifications and invoices are things that
 * arrive, upgrade and settings are occasional.
 */
const PROFILE_MENU = [
  { path: '/greeting-cards', icon: FiMail, label: 'Cards', hint: 'Cards you made and received' },
  { path: '/guest-lists', icon: FiList, label: 'Guest lists', hint: 'Reusable lists of people' },
  { path: '/notifications', icon: FiBell, label: 'Notifications', hint: 'RSVPs, requests and replies' },
  { path: '/invoices', icon: FiFileText, label: 'Invoices', hint: 'Receipts for anything you paid for' },
  { path: '/upgrade', icon: FiTrendingUp, label: 'Upgrade', hint: 'More guests, more photos' },
  { path: '/settings', icon: FiSettings, label: 'Settings', hint: 'Account, privacy, sign out' },
];

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [hostedEvents, setHostedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      let profileData;
      
      if (username) {
        // Load by username
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('username', username)
          .single();
        
        if (error) throw error;
        profileData = data;
      } else if (user) {
        // Load current user's profile
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        profileData = data;
      }

      setProfile(profileData);
      setIsOwnProfile(user?.id === profileData?.id);

      // Load hosted events
      if (profileData) {
        const { data: events } = await supabase
          .from('events')
          .select('*')
          .eq('host_id', profileData.id)
          .eq('status', 'published')
          .order('event_date', { ascending: false })
          .limit(6);
        
        setHostedEvents(events || []);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [username, user]);

  useEffect(() => {
    loadProfile();
  }, [username, user, loadProfile]);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/${profile.username}`;
    const message = `Check out ${profile.first_name}'s profile on Invits!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.first_name} ${profile.last_name} - Invits`,
          text: message,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Profile link copied to clipboard!');
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      instagram: FiInstagram,
      twitter: FiTwitter,
      linkedin: FiLinkedin,
      website: FiGlobe,
    };
    return icons[platform] || FiGlobe;
  };

  const formatDate = (dateString) => {
    return (parseLocalDate(dateString) ?? new Date(NaN)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="error-container">
          <h2>Profile not found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/home')}>
            Go to Explore
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const locationString = [profile.city, profile.state, profile.country]
    .filter(Boolean)
    .join(', ');

  return (
    <DashboardLayout>
      <div className="profile-page">
        <div className="profile-container">
          {/* The header used to hold two unlabelled icon buttons — guest
              lists and settings — which was the whole of this page's
              navigation. The full list is below, where it has room for
              labels. */}

          {/* Profile Card */}
          <motion.div 
            className="profile-card glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="profile-header-section">
              <div className="profile-avatar-wrapper">
                {profile.profile_photo_cdn_url ? (
                  <img 
                    src={profile.profile_photo_cdn_url} 
                    alt={profile.first_name}
                    className="profile-avatar"
                  />
                ) : (
                  <div className="profile-avatar-placeholder">
                    <FiUserIcon size={40} />
                  </div>
                )}
                {isOwnProfile && (
                  <div className="avatar-edit-badge">
                    <FiEdit2 size={14} />
                  </div>
                )}
              </div>

              <div className="profile-info-section">
                <h1 className="profile-name">
                  {profile.first_name} {profile.last_name}
                </h1>
                {profile.username && (
                  <p className="profile-username">@{profile.username}</p>
                )}
                {locationString && (
                  <div className="profile-location">
                    <FiMapPin size={14} />
                    <span>{locationString}</span>
                  </div>
                )}
              </div>
            </div>

            {profile.bio && (
              <p className="profile-bio">{profile.bio}</p>
            )}

            {/* Social Links */}
            {profile.social_links && Object.keys(profile.social_links).length > 0 && (
              <div className="social-links">
                {Object.entries(profile.social_links).map(([platform, handle]) => {
                  const Icon = getSocialIcon(platform);
                  return (
                    <a 
                      key={platform}
                      href={handle}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Stats */}
            <div className="profile-stats">
              <div className="stat-item">
                <div className="stat-value">{profile.hosting_count || 0}</div>
                <div className="stat-label">Hosted</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">{profile.attending_count || 0}</div>
                <div className="stat-label">Attended</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">{profile.vibe_count || 0}</div>
                <div className="stat-label">Vibes</div>
              </div>
            </div>

            {/* Ratings */}
            <div className="profile-ratings">
              <div className="rating-item">
                <div className="rating-icon">
                  <FiHome />
                </div>
                <div className="rating-content">
                  <div className="rating-percentage">
                    {profile.host_rating_count > 0
                      ? `${Math.round((profile.host_rating / 10) * 100)}%`
                      : 'No ratings'}
                  </div>
                  <div className="rating-label">recommended as host</div>
                  {profile.host_rating_count > 0 && (
                    <div className="rating-count">
                      ({profile.host_rating_count} rating{profile.host_rating_count !== 1 ? 's' : ''})
                    </div>
                  )}
                </div>
              </div>

              <div className="rating-item">
                <div className="rating-icon">
                  <FiUserIcon />
                </div>
                <div className="rating-content">
                  <div className="rating-percentage">
                    {profile.attendee_rating_count > 0
                      ? `${Math.round((profile.attendee_rating / 10) * 100)}%`
                      : 'No ratings'}
                  </div>
                  <div className="rating-label">recommended as guest</div>
                  {profile.attendee_rating_count > 0 && (
                    <div className="rating-count">
                      ({profile.attendee_rating_count} rating{profile.attendee_rating_count !== 1 ? 's' : ''})
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="profile-actions">
              {isOwnProfile ? (
                <>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FiEdit2 />
                    <span>Edit Profile</span>
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={handleShare}
                  >
                    <FiShare2 />
                    <span>Share</span>
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" style={{ flex: 1 }}>
                  <FiUsers />
                  <span>Connect</span>
                </button>
              )}
            </div>
          </motion.div>

          {/* Everything the three-tab bar cannot hold.
              Phones only: the desktop sidebar already lists these, and
              repeating them here would be two menus for one set of places. */}
          {isOwnProfile && (
            <motion.nav
              className="profile-menu glass-card"
              aria-label="Your account"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              {PROFILE_MENU.map((item) => (
                <Link key={item.path} to={item.path} className="profile-menu-item">
                  <span className="profile-menu-icon">
                    <item.icon />
                  </span>
                  <span className="profile-menu-text">
                    <strong>{item.label}</strong>
                    <small>{item.hint}</small>
                  </span>
                  <FiChevronRight className="profile-menu-chevron" />
                </Link>
              ))}
            </motion.nav>
          )}

          {/* Hosted Events */}
          {hostedEvents.length > 0 && (
            <motion.div 
              className="hosted-events glass-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="section-header">
                <h3>Hosted Events</h3>
                {hostedEvents.length > 6 && (
                  <Link to="/my-events">View All</Link>
                )}
              </div>

              <div className="events-grid">
                {hostedEvents.map((event) => (
                  <Link 
                    key={event.id}
                    to={`/event/${event.shareable_slug}`}
                    className="event-card"
                  >
                    {event.cover_image_cdn_url && (
                      <div className="event-image">
                        <img src={event.cover_image_cdn_url} alt={event.title} />
                        <div className="event-overlay"></div>
                      </div>
                    )}
                    <div className="event-content">
                      <h4 className="event-title">{event.title}</h4>
                      <div className="event-meta">
                        <div className="event-meta-item">
                          <FiCalendar size={12} />
                          <span>{formatDate(event.event_date)}</span>
                        </div>
                        {event.location_name && (
                          <div className="event-meta-item">
                            <FiMapPin size={12} />
                            <span>{event.location_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {hostedEvents.length === 0 && (
                <div className="empty-state">
                  <FiCalendar size={48} />
                  <p>No events hosted yet</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;